import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import Button from '../ui/Button';
import Skeleton from '../ui/Skeleton';
import AuthLayout, { PasswordField } from '../shell/AuthLayout';
import { clean } from '../shell/copy';

// /talent-setup/:token (HD-9): a performer sets the password for the account the team made for them. Portal auth page on
// AuthLayout, as Login. Same calls: GET /api/auth/talent-setup/:token (name and email), then POST it with { password }.
// States: checking the link, link expired or used (the API's 410), the form, done. Another error on the check leaves the
// form up, as before; the POST then says what went wrong.
export default function TalentSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [talentInfo, setTalentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/api/auth/talent-setup/${token}`)
      .then(r => setTalentInfo(r.data))
      .catch(e => { if (e.response?.status === 410) setExpired(true); })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (password.length < 8) { setErr('Use at least 8 characters.'); return; }
    if (password !== confirm) { setErr('The two passwords do not match. Type the same password twice.'); return; }
    setSaving(true);
    try {
      await api.post(`/api/auth/talent-setup/${token}`, { password });
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.error || 'Your password was not set. Try again.');
    } finally { setSaving(false); }
  }

  if (loading) {
    return (
      <AuthLayout>
        <h1 className="ui-auth__title">Set your password</h1>
        <div className="ui-auth__form" aria-busy="true">
          <Skeleton width="60%" label="Checking your link" />
          <Skeleton variant="block" height="44px" />
          <Skeleton variant="block" height="44px" />
        </div>
      </AuthLayout>
    );
  }

  if (expired) {
    return (
      <AuthLayout>
        <h1 className="ui-auth__title">This link has expired</h1>
        <p className="ui-auth__lede">It is out of date or was already used. Ask the team to send you a new setup email, or sign in if your password is already set.</p>
        <Link className="ui-auth__link" to="/login">Go to sign in</Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <h1 className="ui-auth__title">Your password is set</h1>
        <p className="ui-auth__lede">{talentInfo?.email ? `Sign in with ${talentInfo.email} and your new password.` : 'Sign in with your email and your new password.'}</p>
        <Button variant="primary" portal onClick={() => navigate('/login')}>Go to sign in</Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="ui-auth__title">Set your password</h1>
      <p className="ui-auth__lede">{talentInfo?.name ? `${clean(talentInfo.name)}, choose a password for your Heliconflow account.` : 'Choose a password for your Heliconflow account.'}</p>
      {talentInfo?.email && <p className="ui-auth__meta">Account: {talentInfo.email}</p>}
      <form className="ui-auth__form" onSubmit={handleSubmit} noValidate>
        <PasswordField label="Password" hint="At least 8 characters." value={password} onChange={e => setPassword(e.target.value)}
          autoComplete="new-password" autoFocus data-testid="setup-password" />
        <PasswordField label="Confirm password" value={confirm} onChange={e => setConfirm(e.target.value)}
          autoComplete="new-password" data-testid="setup-confirm" />
        {err && <p className="ui-auth__alert" role="alert">{err}</p>}
        <Button type="submit" variant="primary" portal loading={saving} data-testid="setup-submit">Set password</Button>
      </form>
    </AuthLayout>
  );
}
